// src/components/dashboard/reminders/ManageRemindersPopup.js
import React, { useEffect, useState } from "react";
import "./css/ManageRemindersPopup.css";
import dayjs from "dayjs";
import { apiFetch as fetch } from "apiClient";
import Draggable from "react-draggable";
import { useStackedPopup } from "@dashboard/contexts/PopupStackContext";
import { useGlobalEvents } from "@dashboard/contexts/GlobalEventsContext";

const statusLabel = (r) =>
    r.triggered ? "🔔 TRIGGERED" : "⏱ Scheduled";


const formatDateTime = (value) => {
    if (!value) return "-";
    return dayjs(value).format("YYYY-MM-DD HH:mm");
};

const ManageRemindersPopup = ({ open, onClose }) => {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [newDescription, setNewDescription] = useState("");
    const [newDate, setNewDate] = useState("");
    const [newTime, setNewTime] = useState("");
    const [editingId, setEditingId] = useState(null);

    const {
        reminders: globalReminders,
        hasReminders,
        highlightedReminderId,
        clearHighlightedReminder,
    } = useGlobalEvents();


    useStackedPopup(open, () => onClose());

    useEffect(() => {
        if (!open) return;

        // Always show global reminders first
        if (globalReminders && globalReminders.length > 0) {
            setReminders(globalReminders);
        }

        // Also refresh from backend once
        fetchReminders();
    }, [open, globalReminders]);

    const sortedReminders = [...reminders].sort((a, b) => {
        // First: triggered first
        if (a.triggered && !b.triggered) return -1;
        if (b.triggered && !a.triggered) return 1;

        // Second: dueAt ascending if both scheduled
        const da = a.dueAt ? a.dueAt : "";
        const db = b.dueAt ? b.dueAt : "";
        return da.localeCompare(db);
    });

    const fetchReminders = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`/api/reminders`);

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }
            const data = await res.json();
            const list = Array.isArray(data) ? data : [];

            // newest first by createdAt
            list.sort((a, b) => {
                const ca = a.createdAt || "";
                const cb = b.createdAt || "";
                return cb.localeCompare(ca);
            });

            setReminders(list);
        } catch (e) {
            console.error("Failed to fetch reminders", e);
            setError(e.message || "Failed to fetch reminders.");
        } finally {
            setLoading(false);
        }
    };

    const handleSnooze = async (rem) => {
        try {
            const res = await fetch(`/api/reminders/${rem.id}/snooze`, {
                method: "POST",
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            const updated = await res.json();

            // Update local popup list
            setReminders((prev) =>
                prev.map((r) => (r.id === updated.id ? updated : r))
            );

            // This reminder is no longer triggered now
            if (highlightedReminderId === rem.id) {
                clearHighlightedReminder();
            }

            // We let GlobalEventsProvider update header/REM tab
            // via SSE "remindersChanged" + fresh fetch.

        } catch (e) {
            console.error("Failed to snooze reminder", e);
            setError(e.message || "Failed to snooze reminder.");
        }
    };


    const handleDelete = async (rem) => {
        if (!window.confirm("Delete this reminder permanently?")) return;

        try {
            const res = await fetch(`/api/reminders/${rem.id}`, {
                method: "DELETE",
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            setReminders((prev) => prev.filter((r) => r.id !== rem.id));
            if (highlightedReminderId === rem.id) {
                clearHighlightedReminder();
            }
        } catch (e) {
            console.error("Failed to delete reminder", e);
            setError(e.message || "Failed to delete reminder.");
        }
    };

    const beginEdit = (rem) => {
        setEditingId(rem.id);
        setNewDescription(rem.description);

        if (rem.dueAt) {
            const dt = dayjs(rem.dueAt);
            setNewDate(dt.format("YYYY-MM-DD"));
            setNewTime(dt.format("HH:mm"));
        } else {
            setNewDate("");
            setNewTime("");
        }
    };
    const handleUpdate = async () => {
        if (!editingId) return;

        setError(null);
        if (!newDescription.trim()) {
            setError("Description is required to create a reminder.");
            return;
        }
        if (!newDate) {
            setError("Please select a date for this reminder.");
            return;
        }

        const finalTime = newTime?.trim() !== "" ? newTime : "09:00";

        const payload = {
            description: newDescription.trim(),
            date: newDate,
            time: finalTime,
        };


        try {
            const res = await fetch(`/api/reminders/${editingId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            const updated = await res.json();

            setReminders(prev =>
                prev.map(r => (r.id === updated.id ? updated : r))
            );

            // exit edit mode
            setEditingId(null);
            setNewDescription("");
            setNewDate("");
            setNewTime("");

        } catch (e) {
            console.error("Failed to update reminder", e);
            setError(e.message || "Failed to update reminder.");
        }
    };

    const handleCreate = async () => {
        setError(null);
        if (!newDescription.trim()) {
            setError("Description is required to create a reminder.");
            return;
        }
        if (!newDate) {
            setError("Please select a date for this reminder.");
            return;
        }

        // If time missing, default to 09:00
        const finalTime = newTime?.trim() !== "" ? newTime : "09:00";

        const payload = {
            description: newDescription.trim(),
            date: newDate,
            time: finalTime,
        };


        try {
            const res = await fetch("/api/reminders", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            const saved = await res.json();
            setReminders((prev) => [saved, ...prev]);

            setNewDescription("");
            setNewDate("");
            setNewTime("");
        } catch (e) {
            console.error("Failed to create reminder", e);
            setError(e.message || "Failed to create reminder.");
        }
    };

    if (!open) return null;

    return (
        <div
            className="rem-manager-backdrop"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <Draggable handle=".rem-manager-header">
                <div
                    className="rem-manager-container"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="rem-manager-header">
                        <h2>Manage Reminders</h2>
                        <div className="rem-manager-header-right">
                            <button
                                className="rem-manager-refresh-btn"
                                onClick={fetchReminders}
                            >
                                Refresh
                            </button>
                            <button
                                className="rem-manager-close-btn"
                                onClick={onClose}
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className="rem-manager-body">
                        {/* Create new reminder form */}
                        <div className="rem-create-block">
                            <div className="rem-create-title">
                                New Reminder
                            </div>
                            <div className="rem-create-row">
                                <textarea
                                    className="rem-create-description"
                                    placeholder="Description…"
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    rows={3}
                                    onKeyDown={(e) => {
                                        // Optional: Ctrl+Enter to save
                                        if (e.ctrlKey && e.key === "Enter") {
                                            e.preventDefault();
                                            handleCreate();
                                        }
                                    }}
                                />
                                <input
                                    className="rem-create-date"
                                    type="date"
                                    value={newDate}
                                    onChange={(e) => setNewDate(e.target.value)}
                                />
                                <input
                                    className="rem-create-time"
                                    type="time"
                                    value={newTime}
                                    onChange={(e) => setNewTime(e.target.value)}
                                />
                                <button
                                    className="rem-action-btn"
                                    onClick={editingId ? handleUpdate : handleCreate}
                                >
                                    {editingId ? "Update" : "Save"}
                                </button>

                                {editingId && (
                                    <button
                                        className="rem-action-btn rem-action-btn--secondary"
                                        onClick={() => {
                                            setEditingId(null);
                                            setNewDescription("");
                                            setNewDate("");
                                            setNewTime("");
                                            setError(null); // 🧽 clear validation message
                                        }}

                                    >
                                        Cancel
                                    </button>
                                )}

                            </div>

                            <div className="rem-create-hint">
                                Time is optional — defaults to 09:00.
                            </div>
                        </div>

                        {loading && (
                            <div className="rem-manager-status">
                                Loading reminders…
                            </div>
                        )}
                        {error && (
                            <div className="rem-manager-error">{error}</div>
                        )}

                        {!loading &&
                            !error &&
                            reminders.length === 0 && (
                                <div className="rem-manager-status">
                                    No reminders found.
                                </div>
                            )}

                        {!loading && reminders.length > 0 && (
                            <div className="rem-table-scroll">
                                    <table className="rem-manager-table">

                                    <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th>Due</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Triggered</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {sortedReminders.map((r) => {
                                        const shouldFlash =
                                            r.flash === true || highlightedReminderId === r.id;

                                        const rowClasses =
                                            (r.triggered ? "rem-row-triggered" : "") +
                                            (shouldFlash ? " rem-row-flash" : "");

                                        return (
                                            <tr key={r.id} className={rowClasses}>


                                            <td className="rem-description-cell">{r.description}</td>
                                                <td>
                                                    {formatDateTime(
                                                        r.dueAt
                                                    )}
                                                </td>
                                                <td>{statusLabel(r)}</td>
                                                <td>
                                                    {formatDateTime(
                                                        r.createdAt
                                                    )}
                                                </td>
                                                <td>
                                                    {formatDateTime(
                                                        r.triggeredAt
                                                    )}
                                                </td>
                                                <td className="rem-buttons-cell">
                                                    {r.triggered && (
                                                        <button
                                                            className="rem-action-btn rem-action-btn--secondary"
                                                            onClick={() => handleSnooze(r)}
                                                        >
                                                            Snooze
                                                        </button>
                                                    )}

                                                    <button
                                                        className="rem-action-btn rem-action-btn--secondary"
                                                        onClick={() => beginEdit(r)}
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        className="rem-action-btn"
                                                        onClick={() => handleDelete(r)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>

                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                                </div>
                            )}
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default ManageRemindersPopup;
